import { API_ENDPOINTS } from "@/config/api";
import { fetchApi } from "./api";

// ─── Admin users & subscriptions ──────────────────────────────────────────────

export interface AdminUserSubscription {
  id: number;
  email: string | null;
  name: string | null;
  /** "recon" | "operator" | "analyst" */
  subscription_tier: string;
  /** "none" | "active" | "past_due" | "canceled" */
  subscription_status: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_started_at: string | null;
  subscription_expires_at: string | null;
  serial_lookup_monthly_limit: number;
  serial_lookup_unlimited: boolean;
  is_active: boolean;
  created_at: string;
}

export interface AdminUserListResponse {
  users: AdminUserSubscription[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminSubscriptionMetrics {
  total_users: number;
  by_tier: Record<string, number>;
  by_status: Record<string, number>;
  active_count: number;
  estimated_mrr: number;
}

export interface AdminSubscriptionUpdate {
  subscription_tier?: string | null;
  subscription_status?: string | null;
  serial_lookup_monthly_limit?: number;
  serial_lookup_unlimited?: boolean;
}

export interface AdminUserCreate {
  email: string;
  password: string;
  name?: string | null;
  is_beta_tester?: boolean;
  subscription_tier?: string;
  subscription_status?: string;
  serial_lookup_monthly_limit?: number | null;
  serial_lookup_unlimited?: boolean;
}

export function createUser(
  body: AdminUserCreate,
): Promise<AdminUserSubscription> {
  return fetchApi<AdminUserSubscription>(API_ENDPOINTS.adminUsers, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface ListUsersParams {
  search?: string | undefined;
  tier?: string | undefined;
  status?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function listUsers(
  params?: ListUsersParams,
): Promise<AdminUserListResponse> {
  const search = new URLSearchParams();
  if (params?.search) search.set("search", params.search);
  if (params?.tier) search.set("tier", params.tier);
  if (params?.status) search.set("status", params.status);
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.offset != null) search.set("offset", String(params.offset));
  const query = search.toString();
  const url = query
    ? `${API_ENDPOINTS.adminUsers}?${query}`
    : API_ENDPOINTS.adminUsers;
  return fetchApi<AdminUserListResponse>(url);
}

export function getUser(userId: number): Promise<AdminUserSubscription> {
  return fetchApi<AdminUserSubscription>(API_ENDPOINTS.adminUser(userId));
}

export function updateUserSubscription(
  userId: number,
  patch: AdminSubscriptionUpdate,
): Promise<AdminUserSubscription> {
  return fetchApi<AdminUserSubscription>(
    API_ENDPOINTS.adminUserSubscription(userId),
    { method: "PATCH", body: JSON.stringify(patch) },
  );
}

export function getSubscriptionMetrics(): Promise<AdminSubscriptionMetrics> {
  return fetchApi<AdminSubscriptionMetrics>(
    API_ENDPOINTS.adminSubscriptionMetrics,
  );
}

/** Fixed-choice reasons from AccountDeletionReason in nvg-nexus-api. */
export type AccountDeletionReason =
  | "not_using"
  | "too_expensive"
  | "missing_features"
  | "privacy"
  | "found_alternative"
  | "other";

/** GET /api/admin/users/deletions — matches AdminPendingDeletion. */
export interface AdminPendingDeletion {
  id: number;
  email: string | null;
  name: string | null;
  deletion_requested_at: string;
  deletion_scheduled_for: string;
  deletion_reason: AccountDeletionReason | null;
}

export interface AdminPendingDeletionListResponse {
  deletions: AdminPendingDeletion[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListPendingDeletionsParams {
  limit?: number | undefined;
  offset?: number | undefined;
}

export function listPendingDeletions(
  params?: ListPendingDeletionsParams,
): Promise<AdminPendingDeletionListResponse> {
  const search = new URLSearchParams();
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.offset != null) search.set("offset", String(params.offset));
  const query = search.toString();
  const url = query
    ? `${API_ENDPOINTS.adminUserDeletions}?${query}`
    : API_ENDPOINTS.adminUserDeletions;
  return fetchApi<AdminPendingDeletionListResponse>(url);
}

/** GET /api/admin/environment — matches AdminEnvironmentResponse in nvg-nexus-api. */
export interface AdminEnvironmentResponse {
  environment: string;
}

export async function getEnvironment(): Promise<string> {
  const data = await fetchApi<AdminEnvironmentResponse>(
    API_ENDPOINTS.adminEnvironment,
  );
  if (typeof data.environment !== "string" || !data.environment.trim()) {
    throw new Error("Invalid environment response");
  }
  return data.environment.trim();
}

/** GET /api/admin/cron-schedule — matches AdminCronScheduleStatus in nvg-nexus-api. */
export interface AdminCronScheduleStatus {
  is_cron_enabled: boolean;
  environment: string;
  cron_enabled_override: boolean | null;
}

export function getCronScheduleStatus(): Promise<AdminCronScheduleStatus> {
  return fetchApi<AdminCronScheduleStatus>(API_ENDPOINTS.adminCronSchedule);
}
